import * as React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import Button, { EditButton } from '@components/Button';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';
import PickListOptions from '@components/PicklistOptions';
import Sortable from '@components/Sortable';
import Toggle from '@components/Toggle';

import { fetchActiveAgencies } from '@actions/agency';
import {
  createEntitySection,
  editEntitySection,
  ENTITY_SECTION_CREATE_SUCCESS,
  ENTITY_SECTION_EDIT_SUCCESS,
  fetchEntityIntegrationFields,
  fetchEntitySection,
} from '@actions/entitySection';
import { fetchConfigUnlinkedViolationTypes } from '@actions/violationType';

import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator, { CharacterType } from '@utils/ErrorMessageGenerator';

import VehicleIntegrationConfigModal from './EntityIntegrations/VehicleIntegrationConfigModal';

import './style.scss';

interface IDisplayEntityField {
  id?: number;
  label: string;
  type: CustomFieldType;
  options?: string[];
  isActive: boolean;
  isMergeField: boolean;
  isIncludedInEntityName: boolean;
  isNew: boolean;
  externalMappingField?: string;
  error: {
    label: IFieldValidation;
  };
}

interface IDisplayEntitySection extends IConfigXerceEntitySectionBase {
  fields: IDisplayEntityField[];
  configXerceViolationTypeId: number;
  isCustom: boolean;
  entityNumbering: IEntityNumberingRequest;
  integrationConfig?: IConfigEntityIntegrations;
}

export interface IEntityFormProps {
  show: boolean;
  onClose?: (e: any) => void;
  onSubmit?: (e: any) => void;
  entitySectionId: number;
  agencyId: number;
  // from redux
  activeAgenciesListing: IActiveAgencyListResponse;
  configViolationTypes: IConfigXerceViolationTypeResponse[];
  currentEntitySection: IConfigXerceEntitySectionResponse;
  entityIntegrationFields: IKeyValuePair;
  fetchActiveAgencies: TFetchActiveAgencies;
  fetchConfigUnlinkedViolationTypes: TFetchConfigUnlinkedViolationTypes;
  fetchEntitySection: TFetchEntitySection;
  createEntitySection: TCreateEntitySection;
  editEntitySection: TEditEntitySection;
  fetchEntityIntegrationFields: TFetchEntityIntegrationFields;
}

export interface IEntityFormState {
  entitySection: IDisplayEntitySection;
  violationTypeOptions: Array<{ value: number; label: string }>;
  entityAgencyId: number;
  isEdit: boolean;
  isIntegrationModalOpen: boolean;
  renderIntegrationModal: boolean;
  error: {
    label: IFieldValidation;
    agency: IFieldValidation;
    configXerceViolationTypeId: IFieldValidation;
    prefix: IFieldValidation;
    initialization: IFieldValidation;
  };
  modalErrorMessage: string;
  isPicklistOptionsModalOpen: boolean;
  picklistOptionFieldIndex: number;
  isEntityIntegrationActive: boolean;
}

const errMsgGen = new ErrorMessageGenerator();
const cyberdyneValidator = new CyberdyneValidator();

export class EntityForm extends React.Component<IEntityFormProps, IEntityFormState> {
  readonly noFieldInNameDisplayMsg = 'At least one active field should be included in name display.'; // tslint:disable-line
  readonly noViolationTypesMsg = 'There are no unlinked Violation Types configured for this agency.'; // tslint:disable-line

  constructor(props: IEntityFormProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  componentWillReceiveProps(nextProps: IEntityFormProps) {
    const areConfigViolationTypesAvailable =
      nextProps.configViolationTypes !== this.props.configViolationTypes && nextProps.configViolationTypes;
    const isEdit = !!nextProps.entitySectionId;
    const isCurrentEntitySectionUpdated = nextProps.currentEntitySection !== this.props.currentEntitySection;
    const hasModalOpened = nextProps.show !== this.props.show && nextProps.show;

    if (areConfigViolationTypesAvailable) {
      const violationTypeOptions = this.getViolationTypeOptionsFromProps(nextProps);
      const modalErrorMessage = violationTypeOptions.length === 0 ? this.noViolationTypesMsg : '';

      this.setState({
        violationTypeOptions,
        modalErrorMessage,
      });
    }

    if (isEdit && hasModalOpened) {
      this.props.fetchEntitySection(nextProps.agencyId, nextProps.entitySectionId);

      this.setState({
        isEdit: true,
      });
    } else if (!isEdit && hasModalOpened) {
      this.setState({
        ...this.getEmptyState(),
        entityAgencyId: nextProps.agencyId,
      });

      this.props.fetchConfigUnlinkedViolationTypes(nextProps.agencyId);
    }

    if (isEdit && isCurrentEntitySectionUpdated) {
      const entitySection = this.getDisplayEntitySectionFromProps(nextProps);

      this.setState({
        entitySection,
        entityAgencyId: nextProps.agencyId,
        isEntityIntegrationActive: !!entitySection.integrationConfig,
      });
    }
  }

  componentDidUpdate(prevProps: IEntityFormProps, prevState: IEntityFormState) {
    const {
      entitySection: { integrationConfig },
    } = this.state;
    if (prevState.entitySection.integrationConfig !== integrationConfig) {
      this.props.fetchEntityIntegrationFields(this.props.agencyId, 'VEHICLE');
    }

    if (this.state.isEdit && this.props.currentEntitySection !== prevProps.currentEntitySection) {
      const configViolationTypeLabel = this.props.currentEntitySection
        ? this.props.currentEntitySection.configXerceViolationType.label
        : '';

      const hasIntegrations = configViolationTypeLabel.toUpperCase() === ViolationEntityType.VEHICLE;
      this.setState({
        renderIntegrationModal: hasIntegrations,
      });
    }
  }

  getDisplayEntitySectionFromProps = (nextProps: IEntityFormProps): IDisplayEntitySection => {
    const { currentEntitySection } = nextProps;
    const { id, isActive, isCustom, label, configXerceViolationType, entityNumbering, integrationConfig } =
      currentEntitySection;
    const fields = this.getDisplayEntityFieldsFromEntitySectionProp(currentEntitySection);

    return {
      id,
      isActive,
      label,
      configXerceViolationTypeId: configXerceViolationType.id,
      fields,
      isCustom,
      entityNumbering,
      integrationConfig,
    };
  };

  getDisplayEntityFieldsFromEntitySectionProp = (
    entitySection: IConfigXerceEntitySectionResponse
  ): IDisplayEntityField[] => {
    const { entityFields } = entitySection;
    const existingEntityFields: IDisplayEntityField[] = entityFields.map((entityField) => null);

    entityFields.forEach((entityField) => {
      existingEntityFields[this.getIndexFromSequence(entityField.sequence)] = {
        id: entityField.id,
        label: entityField.label,
        type: entityField.type,
        options: entityField.options,
        isActive: entityField.isActive,
        isMergeField: entityField.isMergeField,
        isIncludedInEntityName: entityField.isIncludedInEntityName,
        isNew: false,
        externalMappingField: entityField.externalMappingField,
        error: {
          label: this.getEmptyErrorState(),
        },
      };
    });

    return existingEntityFields;
  };

  getViolationTypeOptionsFromProps = (nextProps: IEntityFormProps) => {
    const { configViolationTypes } = nextProps;

    return configViolationTypes.map((configViolationType) => ({
      label: configViolationType.label,
      value: configViolationType.id,
    }));
  };

  getEmptyErrorState = (): IFieldValidation => ({
    errorMessage: '',
    errorState: null,
  });

  getEmptyState = (): IEntityFormState => ({
    entitySection: {
      id: null,
      label: '',
      isActive: true,
      configXerceViolationTypeId: null,
      fields: [],
      isCustom: true,
      entityNumbering: {
        prefix: '',
        isYearIncluded: true,
        initialization: 1,
        isResettable: true,
      },
      integrationConfig: null,
    },
    entityAgencyId: null,
    violationTypeOptions: [],
    isEdit: false,
    isIntegrationModalOpen: false,
    renderIntegrationModal: false,
    error: {
      label: this.getEmptyErrorState(),
      agency: this.getEmptyErrorState(),
      configXerceViolationTypeId: this.getEmptyErrorState(),
      prefix: this.getEmptyErrorState(),
      initialization: this.getEmptyErrorState(),
    },
    modalErrorMessage: '',
    isPicklistOptionsModalOpen: false,
    picklistOptionFieldIndex: null,
    isEntityIntegrationActive: false,
  });

  getAgenciesOptions = () => {
    const agencies = this.props.activeAgenciesListing ? this.props.activeAgenciesListing.agencies : [];

    return agencies.map((agency) => ({
      label: agency.name,
      value: agency.id,
    }));
  };

  getMappingFieldsOptions = () => {
    const { entityIntegrationFields } = this.props;

    return Object.keys(entityIntegrationFields).map((key) => ({
      label: entityIntegrationFields[key],
      value: key,
    }));
  };

  onChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;

    if (name === 'configXerceViolationTypeId') {
      const violationType = this.props.configViolationTypes.find((v) => v.id.toString() === value.toString());
      const { label } = violationType;
      const hasIntegrations = label.toUpperCase() === ViolationEntityType.VEHICLE;
      this.setState({
        renderIntegrationModal: hasIntegrations,
        isEntityIntegrationActive: hasIntegrations,
      });
    }

    this.setState((prevState) => ({
      entitySection: {
        ...prevState.entitySection,
        [name]: value,
      },
    }));
  };

  onToggleButtonChange = (fieldIndex: number) => (e: React.SyntheticEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    const isActive = (name === 'isMergeField' || name === 'isIncludedInEntityName') && !!value;

    const isTypePicklist = name === 'type' ? value === CustomFieldType.PICKLIST : false;

    this.setState((prevState) => ({
      entitySection: {
        ...prevState.entitySection,
        fields: [
          ...prevState.entitySection.fields.slice(0, fieldIndex),
          {
            ...prevState.entitySection.fields[fieldIndex],
            [name]: value,
            ...(isActive ? { isActive } : {}),
          },
          ...prevState.entitySection.fields.slice(fieldIndex + 1),
        ],
      },
      isPicklistOptionsModalOpen: isTypePicklist,
      picklistOptionFieldIndex: isTypePicklist ? fieldIndex : null,
    }));
  };

  onAgencyChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const entityAgencyId = parseInt(e.currentTarget.value, 10);

    this.setState(
      {
        entityAgencyId,
        violationTypeOptions: [],
      },
      () => this.props.fetchConfigUnlinkedViolationTypes(entityAgencyId)
    );
  };

  onEntityNumberingChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const inputType = e.currentTarget.type;
    const value =
      inputType === 'number'
        ? isNaN(parseInt(e.currentTarget.value, 10))
          ? null
          : parseInt(e.currentTarget.value, 10)
        : e.currentTarget.value;

    this.setState((prevState) => ({
      entitySection: {
        ...prevState.entitySection,
        entityNumbering: {
          ...prevState.entitySection.entityNumbering,
          [name]: value,
        },
      },
    }));
  };

  getEmptyField = (): IDisplayEntityField => ({
    label: '',
    type: CustomFieldType.TEXT,
    isActive: true,
    isMergeField: false,
    isIncludedInEntityName: false,
    externalMappingField: null,
    isNew: true,
    error: {
      label: this.getEmptyErrorState(),
    },
  });

  onAddField = (e: any) => {
    this.setState((prevState) => ({
      entitySection: {
        ...prevState.entitySection,
        fields: [...prevState.entitySection.fields, this.getEmptyField()],
      },
    }));
  };

  onSortEnd = (updatedFields) => {
    this.setState((prevState) => ({
      entitySection: {
        ...prevState.entitySection,
        fields: updatedFields,
      },
    }));
  };

  onClose = (e) => {
    this.setState(this.getEmptyState(), () => this.props.onClose(e));
  };

  getIndexFromSequence = (sequence: number): number => sequence - 1;
  getSequenceFromIndex = (index: number): number => index + 1;

  getCreateEntityFieldsFromDisplayEntityFields = (displayEntityFields: IDisplayEntityField[]) =>
    displayEntityFields.map((displayEntityField, index) => ({
      label: displayEntityField.label.trim(),
      type: displayEntityField.type,
      isActive: displayEntityField.isActive,
      isMergeField: displayEntityField.isMergeField,
      isIncludedInEntityName: displayEntityField.isIncludedInEntityName,
      options: displayEntityField.options,
      sequence: this.getSequenceFromIndex(index),
      externalMappingField: displayEntityField.externalMappingField,
    }));

  getEditEntityFieldsFromDisplayEntityFields = (
    displayEntityFields: IDisplayEntityField[]
  ): {
    existingEntityFields: IConfigXerceEntityFieldEditRequest[];
    newEntityFields: IConfigXerceEntityFieldRequest[];
  } => {
    const entityFields = {
      existingEntityFields: [],
      newEntityFields: [],
    };

    displayEntityFields.reduce((accumulator, displayEntityField, index) => {
      const field: any = {
        label: displayEntityField.label.trim(),
        type: displayEntityField.type,
        isActive: displayEntityField.isActive,
        isMergeField: displayEntityField.isMergeField,
        isIncludedInEntityName: displayEntityField.isIncludedInEntityName,
        externalMappingField: this.state.isEntityIntegrationActive ? displayEntityField.externalMappingField : null,
        sequence: this.getSequenceFromIndex(index),
      };

      if (field.type === CustomFieldType.PICKLIST) {
        field.options = displayEntityField.options;
      }

      if (displayEntityField.isNew) {
        accumulator.newEntityFields.push(field);
      } else {
        field.id = displayEntityField.id;

        accumulator.existingEntityFields.push(field);
      }

      return accumulator;
    }, entityFields); // tslint:disable-line

    return entityFields;
  };

  getIncludeInNameDisplayText = () => {
    const { fields } = this.state.entitySection;

    return fields
      .reduce((displayText, field) => {
        let newDisplayText = displayText;
        if (field.isIncludedInEntityName) {
          newDisplayText = `${newDisplayText} | ${field.label}`;
        }

        return newDisplayText;
      }, '')
      .slice(3); // tslint:disable-line
  };

  validateForm = () => {
    let isValid = true;
    const { entitySection } = this.state;
    const { entityNumbering } = entitySection;
    const error = this.getEmptyState().error;
    let isIncludedInEntityName = false;

    if (cyberdyneValidator.isEmptyString(entitySection.label)) {
      error.label.errorState = IValidationState.ERROR;
      error.label.errorMessage = errMsgGen.getMandatoryFieldMessage('Label');

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(entitySection.label, 10)) {
      error.label.errorState = IValidationState.ERROR;
      error.label.errorMessage = errMsgGen.getCharacterLimitMessage('Label', 10);

      isValid = false;
    } else if (!cyberdyneValidator.isAlpha(entitySection.label)) {
      error.label.errorState = IValidationState.ERROR;
      error.label.errorMessage = errMsgGen.getLimitedCharacterTypeMessage('Label', CharacterType.ALPHABETS);

      isValid = false;
    }

    if (cyberdyneValidator.isEmptyValue(this.state.entityAgencyId)) {
      error.agency.errorState = IValidationState.ERROR;
      error.agency.errorMessage = errMsgGen.getMandatoryFieldMessage('Agency');

      isValid = false;
    }

    if (cyberdyneValidator.isEmptyValue(entitySection.configXerceViolationTypeId)) {
      error.configXerceViolationTypeId.errorState = IValidationState.ERROR;
      error.configXerceViolationTypeId.errorMessage = errMsgGen.getMandatoryFieldMessage(
        // tslint:disable-line
        'Violation Type'
      );

      isValid = false;
    }

    entitySection.fields.forEach((field) => {
      const fieldError = { ...field.error };
      fieldError.label = this.getEmptyErrorState();

      if (field.type === CustomFieldType.PICKLIST && (!field.options || field.options.length === 0)) {
        fieldError.label.errorState = IValidationState.ERROR;
        fieldError.label.errorMessage = errMsgGen.getPicklistEmptyMessage(
          // tslint:disable-line
          'Picklist Type'
        );
        isValid = false;
      }

      if (cyberdyneValidator.isEmptyString(field.label)) {
        fieldError.label.errorState = IValidationState.ERROR;
        fieldError.label.errorMessage = errMsgGen.getMandatoryFieldMessage('Label');

        isValid = false;
      } else if (cyberdyneValidator.isStringGreaterThanLength(field.label, 30)) {
        fieldError.label.errorState = IValidationState.ERROR;
        fieldError.label.errorMessage = errMsgGen.getCharacterLimitMessage('Label', 30);

        isValid = false;
      } else if (!cyberdyneValidator.isAlphaWithSpaces(field.label)) {
        fieldError.label.errorState = IValidationState.ERROR;
        fieldError.label.errorMessage = errMsgGen.getLimitedCharacterTypeMessage(
          // tslint:disable-line
          'Label',
          CharacterType.ALPHA_WITH_SPACES
        );
        isValid = false;
      }

      isIncludedInEntityName = isIncludedInEntityName || field.isIncludedInEntityName;
      field.error = fieldError;
    });

    if (cyberdyneValidator.isEmptyString(entityNumbering.prefix)) {
      error.prefix.errorState = IValidationState.ERROR;
      error.prefix.errorMessage = errMsgGen.getMandatoryFieldMessage('Prefix');

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(entityNumbering.prefix, 10)) {
      error.prefix.errorState = IValidationState.ERROR;
      error.prefix.errorMessage = errMsgGen.getCharacterLimitMessage('Prefix', 10);

      isValid = false;
    } else if (!cyberdyneValidator.isValidNumberingPrefix(entityNumbering.prefix)) {
      error.prefix.errorState = IValidationState.ERROR;
      error.prefix.errorMessage = 'Invalid Number Prefix';

      isValid = false;
    }

    if (
      entityNumbering.initialization === null ||
      cyberdyneValidator.isEmptyString(String(entityNumbering.initialization))
    ) {
      error.initialization.errorState = IValidationState.ERROR;
      error.initialization.errorMessage = errMsgGen.getMandatoryFieldMessage('Number Initialization');

      isValid = false;
    } else if (
      entityNumbering.initialization < 0 ||
      cyberdyneValidator.isStringGreaterThanLength(String(entityNumbering.initialization), 8)
    ) {
      error.initialization.errorState = IValidationState.ERROR;
      error.initialization.errorMessage = errMsgGen.getCharacterLimitMessage('Number Initialization', 8);

      isValid = false;
    }

    isValid = isValid && isIncludedInEntityName;

    this.setState({
      error,
      entitySection: { ...entitySection },
      modalErrorMessage: !isIncludedInEntityName ? this.noFieldInNameDisplayMsg : '',
    });

    return isValid;
  };

  onSubmit = (e: React.SyntheticEvent<MouseEvent>) => {
    const { entitySection, entityAgencyId, isEntityIntegrationActive } = this.state;
    const { entityNumbering } = entitySection;

    if (!this.validateForm()) {
      return;
    }

    if (this.state.isEdit) {
      const entitySectionId = entitySection.id;
      const { newEntityFields, existingEntityFields } = this.getEditEntityFieldsFromDisplayEntityFields(
        entitySection.fields
      );
      const editEntityRequest: IConfigXerceEntitySectionEditRequest = {
        label: entitySection.label,
        isActive: entitySection.isActive,
        newEntityFields,
        existingEntityFields,
        entityNumbering,
        integrationConfig: this.state.isEntityIntegrationActive ? entitySection.integrationConfig : null,
      };

      this.props.editEntitySection(entityAgencyId, entitySectionId, editEntityRequest).then((action) => {
        if (action.type === ENTITY_SECTION_EDIT_SUCCESS) {
          this.setState(this.getEmptyState(), () => this.props.onSubmit(e));
        }
      });
    } else {
      const createEntityRequest: IConfigXerceEntitySectionRequest = {
        label: entitySection.label,
        isActive: entitySection.isActive,
        isCustom: true,
        configXerceViolationTypeId: entitySection.configXerceViolationTypeId,
        entityFields: this.getCreateEntityFieldsFromDisplayEntityFields(entitySection.fields),
        entityNumbering,
        ...(isEntityIntegrationActive && {
          integrationConfig: entitySection.integrationConfig,
        }),
      };

      this.props.createEntitySection(entityAgencyId, createEntityRequest).then((action) => {
        if (action.type === ENTITY_SECTION_CREATE_SUCCESS) {
          this.setState(this.getEmptyState(), () => this.props.onSubmit(e));
        }
      });
    }
  };

  renderTitle = () => {
    const title = this.state.isEdit ? 'Edit Entity' : 'Create Entity';
    const btnTxt = this.state.isEdit ? 'Save' : 'Create Entity';

    return (
      <div className="entity-form__header">
        <h1 className="case-header__left">{title}</h1>
        <div className="case-header__right">
          <div className="case-header__actions">
            <Button onClick={this.onClose} bsStyle={ButtonStyle.DEFAULT} content="Cancel" />
            <Button onClick={this.onSubmit} bsStyle={ButtonStyle.PRIMARY} content={btnTxt} />
          </div>
        </div>
      </div>
    );
  };

  renderField = (field: IDisplayEntityField, fieldIndex: number) => {
    const actionButtonsOptions = [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ];
    const statusOptions = [
      { value: true, label: 'Active' },
      { value: false, label: 'Inactive' },
    ];
    const typeOptions = [
      { value: CustomFieldType.DATE, label: 'Date' },
      { value: CustomFieldType.PICKLIST, label: 'Picklist' },
      { value: CustomFieldType.TEXT, label: 'Text' },
      { value: CustomFieldType.NUMBER, label: 'Number' },
    ];
    const onManagePickListClick = (e: React.SyntheticEvent<HTMLElement>) =>
      this.setState({
        isPicklistOptionsModalOpen: true,
        picklistOptionFieldIndex: fieldIndex,
      });

    return (
      <div
        key={fieldIndex}
        className={`entity-modal__entity-field ${
          !!this.state.entitySection.integrationConfig ? 'hasExternalDataField' : ''
        }`}
      >
        <Input
          width={10}
          field={{
            label: 'Label',
            type: CustomFieldType.TEXT,
            value: field.label,
            isRequired: true,
          }}
          name="label"
          error={field.error.label}
          onChange={this.onToggleButtonChange(fieldIndex)}
        />
        <Input
          width={10}
          field={{
            label: 'Status',
            type: OtherFieldType.RADIOBUTTON,
            value: field.isActive,
            isRequired: false,
          }}
          name="isActive"
          disabled={field.isIncludedInEntityName || field.isMergeField}
          valueLabelOptions={statusOptions}
          onChange={this.onToggleButtonChange(fieldIndex)}
        />
        <Input
          width={10}
          field={{
            label: 'Merged Field?',
            type: OtherFieldType.RADIOBUTTON,
            value: field.isMergeField,
            isRequired: false,
          }}
          name="isMergeField"
          valueLabelOptions={actionButtonsOptions}
          onChange={this.onToggleButtonChange(fieldIndex)}
        />
        <Input
          width={10}
          field={{
            label: 'Include in name display?',
            type: OtherFieldType.RADIOBUTTON,
            value: field.isIncludedInEntityName,
            isRequired: false,
          }}
          valueLabelOptions={actionButtonsOptions}
          name="isIncludedInEntityName"
          onChange={this.onToggleButtonChange(fieldIndex)}
        />
        <Input
          width={10}
          field={{
            label: 'Type',
            type: CustomFieldType.PICKLIST,
            value: field.type,
            options: typeOptions,
            isRequired: false,
          }}
          name="type"
          disabled={!field.isNew}
          onChange={this.onToggleButtonChange(fieldIndex)}
        />
        {!!this.state.entitySection.integrationConfig && (
          <Input
            field={{
              label: 'External Data Field',
              type: CustomFieldType.PICKLIST,
              isRequired: false,
              value: field.externalMappingField,
              options: this.getMappingFieldsOptions(),
            }}
            width={10}
            name="externalMappingField"
            onChange={this.onToggleButtonChange(fieldIndex)}
          />
        )}
        {field.type === CustomFieldType.PICKLIST && <a onClick={onManagePickListClick}>Manage Picklist</a>}
      </div>
    );
  };

  togglePicklistOptions = () => {
    this.setState((prevState) => ({
      isPicklistOptionsModalOpen: !prevState.isPicklistOptionsModalOpen,
    }));
  };

  onPickListOptionsSubmit = (newOptions: string[]) => {
    this.setState((prevState) => ({
      entitySection: {
        ...prevState.entitySection,
        fields: [
          ...prevState.entitySection.fields.slice(0, prevState.picklistOptionFieldIndex),
          {
            ...prevState.entitySection.fields[prevState.picklistOptionFieldIndex],
            options: newOptions,
          },
          ...prevState.entitySection.fields.slice(prevState.picklistOptionFieldIndex + 1),
        ],
      },
      picklistOptionFieldIndex: null,
      isPicklistOptionsModalOpen: false,
    }));
  };

  renderEntityNumberingContainer = () => {
    const { entitySection, error } = this.state;
    const { entityNumbering } = entitySection;
    const { prefix, isYearIncluded, initialization, isResettable } = entityNumbering;
    const statusOptions = [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ];
    const entityNumberDisplayYear = isYearIncluded ? `${moment().format('YY')}-` : '';
    const entityNumberDisplayPrefix = prefix ? `${prefix}-` : '';
    const entityNumberDisplayInitialization = initialization ? `${initialization}` : '';
    const entityNumberDisplay =
      `${entityNumberDisplayPrefix}` + `${entityNumberDisplayYear}` + `${entityNumberDisplayInitialization}`;

    return (
      <div>
        <h2>Entity Numbering</h2>
        <div className="entity-numbering">
          <Input
            width={12}
            field={{
              label: 'Prefix',
              type: CustomFieldType.TEXT,
              value: prefix,
              isRequired: true,
            }}
            name="prefix"
            error={error.prefix}
            onChange={this.onEntityNumberingChange}
          />
          <Input
            width={12}
            field={{
              label: 'Include Year?',
              type: OtherFieldType.RADIOBUTTON,
              value: isYearIncluded,
              isRequired: false,
            }}
            valueLabelOptions={statusOptions}
            name="isYearIncluded"
            onChange={this.onEntityNumberingChange}
          />
          <Input
            width={12}
            field={{
              label: 'Number Initialization',
              type: CustomFieldType.NUMBER,
              value: initialization,
              isRequired: true,
            }}
            name="initialization"
            error={error.initialization}
            onChange={this.onEntityNumberingChange}
          />
          <Input
            width={12}
            field={{
              label: 'Reset Numbers Each Year?',
              type: OtherFieldType.RADIOBUTTON,
              value: isResettable,
              isRequired: false,
            }}
            valueLabelOptions={statusOptions}
            name="isResettable"
            onChange={this.onEntityNumberingChange}
          />
          <div className="entity-numbering__display">
            <div className="entity-numbering__secondary-header">Entity Number Display:</div>
            <label>{entityNumberDisplay}</label>
          </div>
        </div>
      </div>
    );
  };

  onIntegrationToggleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const checked = e.currentTarget.checked;

    this.setState((prevState) => ({
      ...prevState,
      isEntityIntegrationActive: checked,
      isIntegrationModalOpen: checked,
    }));
  };

  toggleIntegrationConfigModal = () => {
    this.setState((prevState) => ({
      ...prevState,
      isIntegrationModalOpen: !prevState.isIntegrationModalOpen,
    }));
  };

  onIntegrationConfigModalClose = () => {
    this.toggleIntegrationConfigModal();
    this.setState((prevState) => ({
      ...prevState,
      isEntityIntegrationActive: !!prevState.entitySection.integrationConfig,
    }));
  };

  onIntegrationConfigModalSubmit = (integrationConfig: IConfigEntityIntegrations) => {
    this.toggleIntegrationConfigModal();
    this.setState((prevState) => ({
      ...prevState,
      entitySection: {
        ...prevState.entitySection,
        integrationConfig,
      },
    }));
  };

  renderIntegrationsModal = () => (
    <VehicleIntegrationConfigModal
      show={this.state.isIntegrationModalOpen}
      integrationConfig={this.state.entitySection.integrationConfig}
      onClose={this.onIntegrationConfigModalClose}
      onSubmit={this.onIntegrationConfigModalSubmit}
    />
  );

  renderEntityIntegrationConfig = () => {
    const { entitySection } = this.state;
    const integrationConfig = entitySection.integrationConfig && entitySection.integrationConfig.licensePlateService;

    return (
      <div>
        <h2>Automatic License Plate Recognition</h2>
        <div className={'entity-integration-section toggle'}>
          <div className={'toggleAndEditBlock'}>
            <Toggle
              bsStyle="primary"
              onToggle={this.onIntegrationToggleChange}
              toggleState={this.state.isEntityIntegrationActive}
            />
            {this.state.isEntityIntegrationActive && (
              <EditButton
                onClick={() => {
                  this.setState((prevState) => ({
                    ...prevState,
                    isIntegrationModalOpen: true,
                  }));
                }}
              />
            )}
          </div>
          {!!integrationConfig && (
            <>
              <div className={'decoderArea'}>
                <span>Plate decoder URL: {integrationConfig.plateDecoderURL}</span>
              </div>
              <div>
                <span>Plate recognition URL: {integrationConfig.plateRecognitionURL}</span>
              </div>
              <div>
                <span>API key: {integrationConfig.apiKey}</span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  renderFieldsContainer = () => {
    const { fields } = this.state.entitySection;
    const { picklistOptionFieldIndex } = this.state;
    const areFieldsPresent = fields && fields.length > 0;

    return (
      <div
        title="Fields"
        className={`entity-modal__entity-fields-container ${
          areFieldsPresent ? '' : 'entity-modal__entity-fields-container--empty'
        } tile-base`}
      >
        <h2>Fields</h2>
        <div className="entity-modal__entity-fields">
          {areFieldsPresent && (
            <Sortable sortableItems={fields} onSortEnd={this.onSortEnd}>
              {fields.map((field, index) => this.renderField(field, index))}
            </Sortable>
          )}
          <a className="entity-modal__entity-fields-add" onClick={this.onAddField}>
            <i />
            Add Field
          </a>
        </div>
        <PickListOptions
          show={this.state.isPicklistOptionsModalOpen}
          onClose={this.togglePicklistOptions}
          options={picklistOptionFieldIndex !== null ? fields[picklistOptionFieldIndex].options : []}
          onSubmit={this.onPickListOptionsSubmit}
        />
      </div>
    );
  };

  renderBody = () => {
    const statusOptions = [
      { value: true, label: 'Active' },
      { value: false, label: 'Inactive' },
    ];

    const { isEdit, entitySection, entityAgencyId, violationTypeOptions, error } = this.state;
    const configViolationTypeLabel =
      isEdit && this.props.currentEntitySection ? this.props.currentEntitySection.configXerceViolationType.label : '';

    return (
      <div className="entity-modal__body">
        <div className="entity-modal__entity-info tile-base">
          <Input
            width={12}
            field={{
              label: 'Agency',
              type: CustomFieldType.PICKLIST,
              options: this.getAgenciesOptions(),
              value: entityAgencyId,
              isRequired: true,
            }}
            error={error.agency}
            disabled={isEdit}
            name="entityAgencyId"
            onChange={this.onAgencyChange}
          />
          <Input
            width={12}
            field={{
              label: 'Label',
              type: CustomFieldType.TEXT,
              value: entitySection.label,
              isRequired: true,
            }}
            name="label"
            error={error.label}
            onChange={this.onChange}
          />
          <Input
            width={12}
            field={{
              label: 'Violation Type',
              type: isEdit ? CustomFieldType.TEXT : CustomFieldType.PICKLIST,
              value: isEdit ? configViolationTypeLabel : entitySection.configXerceViolationTypeId,
              options: violationTypeOptions,
              isRequired: true,
            }}
            disabled={isEdit}
            error={error.configXerceViolationTypeId}
            name="configXerceViolationTypeId"
            onChange={this.onChange}
          />
          <Input
            width={12}
            field={{
              label: 'Status',
              type: OtherFieldType.RADIOBUTTON,
              value: entitySection.isActive,
              isRequired: false,
            }}
            valueLabelOptions={statusOptions}
            disabled={!entitySection.isCustom}
            name="isActive"
            onChange={this.onChange}
          />
        </div>
        <div className="entity-modal__name-display">
          <div className="entity-modal__name-display-preview">
            <b>Name Display</b>
            {this.getIncludeInNameDisplayText()}
          </div>
          <div className="entity-modal__name-display-disclaimer">
            <i />
            This text is generated by your selection of "Include in Name Display?" in the fields below. This text will
            appear as the unique label for each instance of this entity
          </div>
        </div>
        {this.renderEntityNumberingContainer()}
        {this.state.renderIntegrationModal && this.renderEntityIntegrationConfig()}
        <span className="modal-body-br" />
        {this.renderFieldsContainer()}
        {this.renderIntegrationsModal()}
      </div>
    );
  };

  render() {
    return (
      <ModalBase
        show={this.props.show}
        onClose={this.onClose}
        titleElement={this.renderTitle()}
        bodyElement={this.renderBody()}
        footerElement={null}
        className="entity-form"
        errorMessage={this.state.modalErrorMessage}
      />
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  activeAgenciesListing: state.agency.activeAgenciesListing,
  configViolationTypes: state.violationType.configViolationTypes,
  currentEntitySection: state.entitySection.currentEntitySection,
  entityIntegrationFields: state.entitySection.entityIntegrationFields,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchActiveAgencies: () => dispatch(fetchActiveAgencies()),
  fetchConfigUnlinkedViolationTypes: (agencyId: number) => dispatch(fetchConfigUnlinkedViolationTypes(agencyId)),
  fetchEntitySection: (agencyId: number, entitySectionId: number) =>
    dispatch(fetchEntitySection(agencyId, entitySectionId)),
  createEntitySection: (agencyId: number, entitySection: IConfigXerceEntitySectionRequest) =>
    dispatch(createEntitySection(agencyId, entitySection)),
  editEntitySection: (agencyId: number, entitySectionId: number, entitySection: IConfigXerceEntitySectionEditRequest) =>
    dispatch(editEntitySection(agencyId, entitySectionId, entitySection)),
  fetchEntityIntegrationFields: (agencyId: number, entityType: string) =>
    dispatch(fetchEntityIntegrationFields(agencyId, entityType)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EntityForm);
